/**
 * The intro's time base. `intro-machine` is a pure function of elapsed
 * milliseconds, so the only thing that can desynchronise the sequence from the
 * music is *which* milliseconds it is told about — and the two ways that goes
 * wrong are both real: `performance.now()` read before the activation gesture
 * would have the sequence already running by the time audio unlocks, and a
 * hidden tab freezes `requestAnimationFrame` while wall clock keeps going, so
 * the first frame back would fire a dozen ticks at once against music that had
 * moved on.
 *
 * So: a clock that starts on command, and that can be paused and resumed with
 * the paused span deducted. The time source is injected, which is also what
 * makes every timing path testable without waiting for real seconds.
 */

export interface IntroClock {
  /** (Re)starts from zero. */
  start(): void;
  /** Milliseconds since `start`, minus every paused span. Zero before the first `start`. */
  elapsedMs(): number;
  pause(): void;
  resume(): void;
  paused(): boolean;
  running(): boolean;
  stop(): void;
}

function defaultNow(): number {
  return performance.now();
}

export function createIntroClock(now: () => number = defaultNow): IntroClock {
  let startedAt = 0;
  let pausedAt: number | undefined;
  let pausedForMs = 0;
  let active = false;

  return {
    start() {
      startedAt = now();
      pausedAt = undefined;
      pausedForMs = 0;
      active = true;
    },
    elapsedMs() {
      if (!active) return 0;
      // `Math.max` guards the one case the injected source could regress on: a
      // resume whose timestamp predates the pause it is closing.
      return Math.max(0, (pausedAt ?? now()) - startedAt - pausedForMs);
    },
    pause() {
      if (!active || pausedAt !== undefined) return;
      pausedAt = now();
    },
    resume() {
      if (pausedAt === undefined) return;
      pausedForMs += Math.max(0, now() - pausedAt);
      pausedAt = undefined;
    },
    paused: () => pausedAt !== undefined,
    running: () => active,
    stop() {
      active = false;
      pausedAt = undefined;
    },
  };
}
